import { ScheduleItem } from '../types/scheduleItem.types';
import { TimeScale } from '../types/calendar.types';

// Generate time slots from 12:00 AM to 11:00 PM
export function generateTimeSlots(timeScaleMinutes: TimeScale): string[] {
  const slots: string[] = [];
  const startHour = 0;  // 12:00 AM
  const endHour = 23;   // 11:00 PM
  
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += timeScaleMinutes) {
      // Don't go past 11:00 PM
      if (hour === endHour && minute > 0) break;
      
      const formattedHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const amPm = hour < 12 ? 'AM' : 'PM';
      const formattedMinute = minute.toString().padStart(2, '0');
      
      slots.push(`${formattedHour}:${formattedMinute} ${amPm}`);
    }
  }
  
  return slots;
}

// Get pixel height per time scale (for vertical positioning)
// Base: 60px per hour at 60-min scale
export function getPixelsPerHour(timeScaleMinutes: TimeScale): number {
  const baseHeight = 60;  // 60px per hour at 60-min scale
  return (60 / timeScaleMinutes) * baseHeight;
}

// Get the number of slots per hour for a given time scale
export function getSlotsPerHour(timeScaleMinutes: TimeScale): number {
  return 60 / timeScaleMinutes;
}

// Calculate top position (in pixels) from a date/time
export function calculateTopPosition(
  date: Date, 
  timeScaleMinutes: TimeScale,
  dayStartHour: number = 0
): number {
  const hour = date.getHours();
  const minute = date.getMinutes();
  const pixelsPerHour = getPixelsPerHour(timeScaleMinutes);
  
  const hoursFromStart = hour - dayStartHour;
  const minuteFraction = minute / 60;
  
  return (hoursFromStart + minuteFraction) * pixelsPerHour;
}

// Calculate height (in pixels) from duration
export function calculateHeightFromDuration(
  startDate: Date,
  endDate: Date,
  timeScaleMinutes: TimeScale
): number {
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationHours = durationMs / (1000 * 60 * 60);
  const pixelsPerHour = getPixelsPerHour(timeScaleMinutes);
  
  return Math.max(durationHours * pixelsPerHour, 20); // Minimum 20px height
}

// Check if two time ranges overlap
export function doTimeRangesOverlap(
  start1: Date, end1: Date,
  start2: Date, end2: Date
): boolean {
  return start1 < end2 && end1 > start2;
}

// Get date range for a view
export function getDateRangeForView(
  currentDate: Date,
  viewType: 'day' | 'week' | 'workweek' | 'month',
  dayViewDays: number = 1
): { start: Date; end: Date; dates: Date[] } {
  // Normalize currentDate to start of day to avoid time-related issues
  const normalizedDate = new Date(currentDate);
  normalizedDate.setHours(0, 0, 0, 0);
  
  const start = new Date(normalizedDate);
  let end: Date;
  let dates: Date[] = [];
  
  switch (viewType) {
    case 'day':
      const daysToShow = Math.max(1, Math.min(7, dayViewDays));
      end = new Date(start);
      end.setDate(end.getDate() + daysToShow - 1);
      dates = getDatesInRange(start, end);
      break;
    case 'week':
      // Sunday to Saturday
      start.setDate(start.getDate() - start.getDay());  // Go to Sunday
      end = new Date(start);  // Create end from the modified start
      end.setDate(end.getDate() + 6);  // Saturday
      dates = getDatesInRange(start, end);
      break;
    case 'workweek':
      // Monday to Friday
      const dayOfWeek = start.getDay();
      const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      start.setDate(start.getDate() + daysToMonday);
      end = new Date(start);  // Create end from the modified start
      end.setDate(end.getDate() + 4);  // Friday
      dates = getDatesInRange(start, end);
      break;
    case 'month':
      start.setDate(1);
      const year = start.getFullYear();
      const month = start.getMonth();
      end = new Date(year, month + 1, 0);  // Last day of month
      // Include days before the 1st to align with Sunday
      const firstDayOfMonth = start.getDay();
      start.setDate(start.getDate() - firstDayOfMonth);
      // Include days after the last day to complete the week
      const lastDayOfMonth = end.getDay();
      end.setDate(end.getDate() + (6 - lastDayOfMonth));
      dates = getDatesInRange(start, end);
      break;
    default:
      end = new Date(start);
      dates = [start];
  }
  
  // Set end to end of day (23:59:59.999) for API filtering
  // This ensures items scheduled later in the day are included
  end.setHours(23, 59, 59, 999);
  
  return { start, end, dates };
}

// Get all dates in a range (inclusive)
export function getDatesInRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const current = new Date(start);
  
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}

// Format date for display
export function formatDate(date: Date, format: 'short' | 'long' | 'dayName' = 'short'): string {
  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'long':
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    case 'dayName':
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    default:
      return date.toLocaleDateString();
  }
}

// Format time for display
export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// Format duration between two dates
export function formatDuration(start: Date, end: Date): string {
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.round(diffMs / (1000 * 60));
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  
  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
}

// Snap a date to the nearest time slot
export function snapToTimeSlot(date: Date, timeScaleMinutes: TimeScale): Date {
  const snapped = new Date(date);
  const minutes = snapped.getMinutes();
  const slotSize = timeScaleMinutes;
  const snappedMinutes = Math.round(minutes / slotSize) * slotSize;
  
  snapped.setMinutes(snappedMinutes);
  snapped.setSeconds(0);
  snapped.setMilliseconds(0);
  
  return snapped;
}

// Check if a date is today
export function isToday(date: Date): boolean {
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
}

// Check if two dates are the same day
export function isSameDay(date1: Date | string | null | undefined, date2: Date | string | null | undefined): boolean {
  if (!date1 || !date2) return false;
  
  // Convert to Date if string
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  
  // Check for invalid dates
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;
  
  return d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();
}

// Get the start of the day (midnight)
export function getStartOfDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

// Get the end of the day (11:59:59)
export function getEndOfDay(date: Date): Date {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Convert a Date to local ISO string WITHOUT timezone conversion.
 * 
 * IMPORTANT: This is part of our timezone-agnostic strategy:
 * - Database stores naive datetime (no timezone info)
 * - We send dates without timezone suffix (no Z, no +HH:MM)
 * - Backend saves exactly what we send
 * - When reading, we parse without timezone conversion (see parseLocalDate in scheduleItemApi.ts)
 * 
 * Example: If user selects "10:00 AM", we send "2026-04-10T10:00:00.000"
 * and database stores "2026-04-10 10:00:00", displayed as "10:00 AM" everywhere.
 * 
 * DO NOT use .toISOString() which converts to UTC!
 */
export function toLocalISOString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
}
